import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { PropsWithChildren, useState } from 'react';
import { CReferralIdentifier, CReferralIdentifierType } from 'src/apis/api-client';

export type FiestaReferralCardProps = {
  data: CReferralIdentifier;
  textAlignment?: 'center' | 'left';
}

export function FiestaReferralCard(props: PropsWithChildren<FiestaReferralCardProps>) {
  const { data } = props;

  const [copied, setCopied] = useState(false);

  const isLink = data.type === CReferralIdentifierType.LINK;
  const isVoucher = data.type === CReferralIdentifierType.VOUCHER;

  // Non-displayable, FollowUp, and Technical types render nothing.
  if (!isLink && !isVoucher) return null;

  async function copyToClipboard() {
    if (!data.value) return;
    try {
      await navigator.clipboard.writeText(data.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for browsers that deny clipboard permission
      try {
        const textarea = document.createElement('textarea');
        textarea.value = data.value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        // Copy failed completely — no feedback shown
      }
    }
  }

  if (isLink) {
    return (
      <Card className='flex flex-column align-items-center p-4 text-center'>
        <span className='flex text-md font-bold mb-3'>
          {t(`common.referralLabels.${data.label}`)}
        </span>
        <div className='flex flex-row gap-2 justify-content-center flex-wrap'>
          <Button
            label={t('shared.components.referral-card.labelOpen')}
            icon='pi pi-external-link'
            className='p-button-outlined'
            severity='success'
            onClick={() => window.open(data.value ?? '', '_blank', 'noopener,noreferrer')}
          />
          <Button
            label={copied ? t('shared.components.referral-card.clickMessage') : t('shared.components.referral-card.labelCopyLink')}
            icon={copied ? 'pi pi-check' : 'pi pi-copy'}
            className='p-button-outlined p-button-secondary'
            onClick={copyToClipboard}
          />
        </div>
      </Card>
    );
  }

  // Voucher — copy to clipboard
  return (
    <Card className='flex flex-column align-items-center p-4 text-center'>
      <span className='flex text-md font-bold mb-3'>
        {t('shared.components.referral-card.labelClick')}
      </span>
      <Button
        label={copied ? t('shared.components.referral-card.clickMessage') : t(`common.referralLabels.${data.label}`)}
        icon={copied ? 'pi pi-check' : 'pi pi-copy'}
        className='p-button-outlined'
        severity='success'
        onClick={copyToClipboard}
        tooltip={copied ? undefined : t('shared.components.referral-card.labelClick')}
        tooltipOptions={{ event: 'hover', position: 'bottom' }}
      />
    </Card>
  );
}

export default FiestaReferralCard;
